import { useQuery } from "@apollo/client";
import { isCalifornia } from "encamp-shared/src/constants/states";
import { KnownSubmissionDocumentTypes } from "encamp-shared/src/documents";
import { useMemo } from "react";

import type { ExistingDefaultDocument } from "../types";

import { FACILITY_CURRENT_SUBMISSION_DOCUMENTS } from "../queries";

const SUBMISSION_DOCUMENT_TYPES = new Set<string>(KnownSubmissionDocumentTypes);

/**
 * The facility's current submission documents of the chosen type, and whether
 * the facility is in California — what the "Use this document on future
 * reports" replacement notice needs.
 */
export function useCurrentSubmissionDefaults({
  tenantId,
  facilityId,
  documentType,
  state,
  open,
}: {
  tenantId: string;
  facilityId: string | undefined;
  documentType: string | null | undefined;
  state: string | null | undefined;
  open: boolean;
}): { existingDefaults: ExistingDefaultDocument[]; isCalifornia: boolean } {
  const isSubmissionType = documentType != null && SUBMISSION_DOCUMENT_TYPES.has(documentType);
  const { data } = useQuery(FACILITY_CURRENT_SUBMISSION_DOCUMENTS, {
    variables: {
      search: `tenantId:${tenantId} facilityId:${facilityId} documentType:${documentType} excludeRejectedSubmissionDocuments:true`,
    },
    skip: !open || !facilityId || !isSubmissionType,
    // Uploads earlier in the session can change which document is current.
    fetchPolicy: "cache-and-network",
  });

  return useMemo(() => {
    const currentDocuments = (data?.documents.items ?? []).filter(
      (d) => d.facilityDocument?.currentSubmissionDocument === true
    );
    const facilityState =
      state ??
      currentDocuments.flatMap((d) => d.facilities ?? []).find((f) => f.id === facilityId)?.state;
    return {
      existingDefaults: currentDocuments.map((d) => ({
        id: d.id,
        documentType: d.documentType,
        title: d.title,
      })),
      isCalifornia: isCalifornia(facilityState),
    };
  }, [data, facilityId, state]);
}
